var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { Vectors, randomWeightedItem } from "@project-rouge/rg-three";
import { Vector3, Line3 } from "three";
import { getLineLengthWeightMod } from "./index191.js";
import { mainLoadOptions } from "./index247.js";
import { Side } from "./index242.js";
import { ThreadCollider } from "./index252.js";
import { ThreadData } from "./index197.js";
const TOL = 1e-5;
const _cross = new Vector3();
class ThreadController {
  constructor(props) {
    __publicField(this, "collider");
    __publicField(this, "data");
    __publicField(this, "context");
    this.collider = new ThreadCollider(props);
    this.data = this.collider.data;
    this.context = this.data.context;
  }
  /** Creates a main `ThreadController`
   *
   * If `start` and `dir` are defined these will be used to create a `ThreadController`,
   * otherwise:
   * double load will be prioritised and all edges will be tried
   * (based on weighted random selection).
   * Only if no double load thread can be created will single load be attempted.
   * You can change the logic by passing a different `loadOptions` array.
   */
  static createMain({
    context,
    boundary,
    holes,
    start,
    dir,
    rng,
    loadOptions = mainLoadOptions.dynamic,
    edgeWeightMods = [getLineLengthWeightMod()],
    maxWingLength
  }) {
    if (!start || !dir) {
      for (const load of loadOptions) {
        const thread2 = ThreadController.createMainFromBoundary({
          boundary,
          holes,
          rng,
          context,
          load,
          edgeWeightMods,
          maxWingLength
        });
        if (thread2)
          return thread2;
      }
      return;
    }
    const thread = new ThreadController({
      load: void 0,
      bidirectional: true,
      start,
      dir,
      context,
      boundary,
      holes,
      maxWingLength
    });
    if (!thread.isValidStart())
      return;
    return thread.grow();
  }
  static createMainFromBoundary({ boundary, holes, rng, load, context, edgeWeightMods, maxWingLength }) {
    const lines = boundaryToLines(boundary);
    let partialProps;
    while (partialProps === void 0 && lines.length > 0) {
      const line = popRandomLine({ lines, rng, edgeWeightMods });
      if (!line)
        return;
      partialProps = getStartFromLine(line, context, load);
    }
    if (!partialProps)
      return;
    const thread = new ThreadController({
      load,
      bidirectional: true,
      start: partialProps.start,
      dir: partialProps.dir,
      context,
      boundary,
      holes,
      maxWingLength
    });
    if (!thread.isValidStart())
      return;
    return thread.grow();
  }
  /** Add thread at the end (or start) and the right (or left) of current thread */
  add({ start, right, load }) {
    if (!this.canAdd({ start, right, load }))
      return;
    this.data.shrinkBranchToBranch();
    const point = (start ? this.data.start : this.data.end).clone();
    const dir = start ? this.data.dir : this.data.nDir;
    const depths = ThreadData.getDepths(this.context, load);
    const moveBack = start === right ? depths.right : depths.left;
    const moveRight = right ? this.data.depthRight : -this.data.depthLeft;
    point.addScaledVector(dir, moveBack);
    point.addScaledVector(this.data.right, moveRight);
    const threadDir = this.data.right.clone();
    if (!right)
      threadDir.negate();
    const props = {
      start: point,
      dir: threadDir,
      boundary: this.data.boundary,
      holes: this.data.holes,
      load,
      context: this.context,
      maxWingLength: this.data.maxWingLength
    };
    const thread = new ThreadController(props);
    thread.data.startThread = {
      thread: this.data,
      right: start ? !right : right
    };
    const branch = thread.grow();
    if (branch)
      return branch;
    this.data.unshrinkBranchToBranch();
  }
  /** saves the thread into the one it grow from */
  save({ thread, start }) {
    this.data.save({ data: thread.data, start });
  }
  /** grows and returns the grown thread if valid, otherwise returns `undefined` */
  grow() {
    if (this.data.bidirectional)
      this._grow({ forward: false });
    this._grow({ forward: true });
    if (!this.isValidGrowth())
      return;
    return this;
  }
  _grow({ forward }) {
    const size = this.collider.getGrowLimit(forward) - TOL;
    const point = forward ? this.data.end : this.data.start;
    const dir = forward ? this.data.dir : this.data.nDir;
    point.addScaledVector(dir, size);
    this.data.updateSize();
  }
  // @todo different sizes depending on core requirements (eg above 11 floors)
  isValidGrowth() {
    if (this.data.isMain)
      return this.data.size >= this.context.minLineBuilding;
    return this.data.size >= this.context.minBranchLength;
  }
  /**
   * check if is outside hole and inside boundary
   * check only required for the first thread
  */
  isValidStart() {
    if (this.collider.isInsideHole())
      return false;
    if (!this.collider.isInsideBoundary())
      return false;
    return true;
  }
  /** check can branch a new thread from main */
  canAdd({ start, right, load }) {
    if (!this.passTBranchCheck(start))
      return false;
    if (!this.passFace2FaceCheck({ start, right, load }))
      return false;
    if (!this.passMainMinSizeCheck())
      return false;
    if (!this.passCanTurnCheck())
      return false;
    if (!this.passGrowEnds(start))
      return false;
    if (!this)
      return false;
    return true;
  }
  /** we don't allow branches if the main branch is too small to turn */
  passCanTurnCheck() {
    return this.data.size >= this.context.canTurnMinSize;
  }
  /** ensure that branches only grow from free ends */
  passGrowEnds(start) {
    if (!this.data.bidirectional && start)
      return false;
    return true;
  }
  /** we don't allow T branches */
  passTBranchCheck(start) {
    if (start && this.data.startThread)
      return false;
    if (!start && this.data.endThread)
      return false;
    return true;
  }
  /** check if main is big enough for 1 or 2 branches */
  passMainMinSizeCheck() {
    if (this.data.startThread || this.data.endThread)
      return this.context.min2CornerWing <= this.data.size;
    return this.context.min1CornerWing <= this.data.size;
  }
  passFace2FaceCheck({ start, right, load }) {
    const oppositeThread = start ? this.data.endThread : this.data.startThread;
    if (!oppositeThread)
      return true;
    if (oppositeThread.right !== right)
      return true;
    const depths = ThreadData.getDepths(this.context, load);
    let f2f;
    if (this.data.bidirectional) {
      f2f = this.data.size + 2 * TOL - oppositeThread.thread.depth - depths.left - depths.right;
    } else {
      f2f = this.data.size + 2 * TOL - depths.left - depths.right;
    }
    const f2fRequired = Math.max(this.context.face2face, this.context.minApartmentWidth);
    return f2f >= f2fRequired;
  }
}
function getStartFromLine(line, context, load) {
  const start = line.getCenter(new Vector3());
  const dir = line.delta(new Vector3()).normalize();
  _cross.crossVectors(dir, Vectors.Up);
  let offset = TOL;
  if (load === Side.right)
    offset += context.singleSmallSide;
  else
    offset += context.singleBigSide;
  start.addScaledVector(_cross, offset);
  return { start, dir, load };
}
function popRandomLine({ lines, rng, edgeWeightMods }) {
  const list = lines.map((_, i2) => i2);
  const weights = getWeights({ lines, edgeWeightMods });
  if (weights.reduce((sum, w) => sum + w, 0) === 0) {
    lines.length = 0;
    return;
  }
  let i = 0;
  if (rng) {
    i = randomWeightedItem({ list, weights, rng });
  } else {
    const bestWeight = Math.max(...weights);
    const bestIndex = weights.indexOf(bestWeight);
    i = bestIndex;
  }
  const line = lines[i];
  lines.splice(i, 1);
  return line;
}
function getWeights({ lines, edgeWeightMods }) {
  const weights = new Array(lines.length).fill(1);
  for (const mod of edgeWeightMods) {
    const modWeights = mod(lines);
    for (let i = 0; i < lines.length; i++) {
      weights[i] *= modWeights[i];
    }
  }
  return weights;
}
function boundaryToLines(boundary) {
  const lines = [];
  for (let i = 1; i < boundary.length; i++) {
    const line = new Line3(boundary[i - 1].clone(), boundary[i].clone());
    lines.push(line);
  }
  return lines;
}
export {
  ThreadController
};
//# sourceMappingURL=index248.js.map
